import { Favorite, FavoriteBorder } from '@mui/icons-material';
import { Box, Card, CardContent, CardMedia, CircularProgress, Grid, IconButton, Tooltip, Typography, alpha, styled, useTheme } from '@mui/material';
import { AUTHORITIES } from 'app/config/constants';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import TruckImplementModal from 'app/modules/truck-implement/TruckImplementModal';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { NoData } from 'app/shared/components/no-data/NoData';
import { NoImage } from 'app/shared/components/no-image/NoImage';
import { PageFilters } from 'app/shared/components/page-filters/PageFilters';
import { PageHeader } from 'app/shared/components/page-header/PageHeader';
import { PaginationComponent } from 'app/shared/components/pagination/Pagination';
import { ICompany } from 'app/shared/model/company.model';
import { StatusFilter } from 'app/shared/model/enumerations/status-filter.model';
import { ITruckImplement } from 'app/shared/model/truck-implement.model';
import { getSensorDataBySensorIdsAndTruckImplementIds } from 'app/shared/reducers/shared/sensor-data-values.reducer';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { Loading } from 'app/shared/util/loading';
import { ASC, DESC, SMALL_ITEMS_PER_PAGE } from 'app/shared/util/pagination.constants';
import React, { useEffect, useState } from 'react';
import { getSortState } from 'react-jhipster';
import { RouteComponentProps } from 'react-router-dom';
import { createEntity } from '../favorite-truck/favorite-truck.reducer';
import {
  getRepresentativeTruckImplementByCompanyAndSearch,
  reset as resetRepresentativeTruckImplement,
} from '../representative-truck-implement/representative-truck-implement.reducer';
import { getAllByCompanyIdAndLicensePlate, reset } from './truck-implement.reducer';
import './truckImplement.scss';
import { getOdometerSummaries } from 'app/shared/reducers/shared/truck-odometer.reducer';

export const TruckImplementCard = (props: RouteComponentProps<{ url: string }>) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getSortState(props.location, SMALL_ITEMS_PER_PAGE, 'licensePlate'), props.location.search)
  );
  const urlParams = new URLSearchParams(props.location.search);
  const [searchLicensePlate, setSearchLicensePlate] = useState<string>(urlParams.get('searchLicensePlate') || '');
  const [searchCompanyName, setSearchCompanyName] = useState<string>(urlParams.get('searchCompanyName') || '');
  const [searchDescription, setSearchDescription] = useState(urlParams.get('searchDescription') || '');
  const [searchRepresentative, setSearchRepresentative] = useState<string>(urlParams.get('searchRepresentative') || '');
  const [selectedTruckImplement, setSelectedTruckImplement] = useState<ITruckImplement>(null);
  const [status, setStatus] = useState(urlParams.get('status') || StatusFilter.ACTIVATED);
  const [favorites, setFavorites] = useState<{ [key: string]: boolean }>({});

  const isRepresentativeAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.REPRESENTATIVE_ADMIN]));
  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN]));
  const isManufacturerAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.MANUFACTURER_ADMIN]));
  const sensorDataValuesDTO = useAppSelector(state => state.sensorDataValues.sensorDataValuesByTuckImplementIdsDTO);
  const loadingSensorDataValues = useAppSelector(state => state.sensorDataValues.loading);
  const truckImplementList = useAppSelector(state => state.truckImplement.entities);
  const totalItems = useAppSelector(state => state.truckImplement.totalItems);
  const loading = useAppSelector(state => state.truckImplement.loadingList);
  const totalItemsRepresentative = useAppSelector(state => state.representativeTruckImplement.totalItems);
  const loadingRepresentative = useAppSelector(state => state.representativeTruckImplement.loading);
  const representativeTruckImplementList = useAppSelector(state => state.representativeTruckImplement.entities);
  const truckImplementOdometerList = useAppSelector(state => state.truckOdometer.entities);
  const { match } = props;

  useEffect(() => {
    dispatch(resetRepresentativeTruckImplement());
    dispatch(reset());
  }, []);

  const getAllEntities = () => {
    if (isManufacturerAdmin || isRepresentativeAdmin) {
      dispatch(
        getRepresentativeTruckImplementByCompanyAndSearch({
          page: paginationState.activePage - 1,
          size: SMALL_ITEMS_PER_PAGE,
          sort: `truckImplement.${paginationState.sort},${paginationState.order}`,
          searchCompanyName,
          searchRepresentative,
          searchLicensePlate,
          searchDescription,
          loading: loadingRepresentative,
          status,
        })
      );
    } else {
      dispatch(
        getAllByCompanyIdAndLicensePlate({
          page: paginationState.activePage - 1,
          size: SMALL_ITEMS_PER_PAGE,
          sort: `${paginationState.sort},${paginationState.order}`,
          searchCompanyName,
          searchLicensePlate,
          searchDescription,
          loading,
          status,
        })
      );
    }
  };

  useEffect(() => {
    const searchParams = [];
    if (searchCompanyName !== '') {
      searchParams.push(`searchCompanyName=${encodeURIComponent(searchCompanyName)}`);
    }
    if (searchLicensePlate !== '') {
      searchParams.push(`searchLicensePlate=${encodeURIComponent(searchLicensePlate)}`);
    }
    if (searchRepresentative !== '') {
      searchParams.push(`searchRepresentative=${encodeURIComponent(searchRepresentative)}`);
    }
    if (status !== StatusFilter.ACTIVATED) {
      searchParams.push(`status=${encodeURIComponent(status)}`);
    }

    const searchQueryString = searchParams.length > 0 ? `&${searchParams.join('&')}` : '';

    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}${searchQueryString}`;

    if (props.location.search !== endURL) {
      props.history.replace(`${props.location.pathname}${endURL}`);
    }
  }, [paginationState, searchCompanyName, searchLicensePlate, searchRepresentative, status]);

  useEffect(() => {
    getAllEntities();
  }, [paginationState, status, isManufacturerAdmin, isRepresentativeAdmin]);

  useEffect(() => {
    if (totalItems > 0 && paginationState.activePage > Math.ceil(totalItems / paginationState.itemsPerPage)) {
      setPaginationState({
        ...paginationState,
        activePage: 1,
      });
    }
  }, [totalItems, paginationState.activePage, paginationState.itemsPerPage]);

  useEffect(() => {
    if (truckImplementList && truckImplementList.length > 0) {
      const truckImplementIds = truckImplementList.map(ti => ti.id);

      const debounce = setTimeout(() => {
        dispatch(getSensorDataBySensorIdsAndTruckImplementIds({ truckImplementIds, loadingSensorDataValues, onSensorDataValues: false }));
      }, 500);

      dispatch(getOdometerSummaries(truckImplementIds));

      const initialFavorites = {};
      truckImplementList.forEach(ti => {
        initialFavorites[ti.id] = ti.favorite;
      });
      setFavorites(initialFavorites);

      return () => clearTimeout(debounce);
    }
  }, [truckImplementList]);

  useEffect(() => {
    if (representativeTruckImplementList && representativeTruckImplementList.length > 0) {
      const truckImplementIds = representativeTruckImplementList.map(ti => ti.truckImplement.id);

      const debounce = setTimeout(() => {
        dispatch(getSensorDataBySensorIdsAndTruckImplementIds({ truckImplementIds, loadingSensorDataValues, onSensorDataValues: false }));
      }, 500);

      const initialFavorites = {};
      representativeTruckImplementList.forEach(ti => {
        initialFavorites[ti.truckImplement.id] = ti.truckImplement.favorite;
      });
      setFavorites(initialFavorites);

      return () => clearTimeout(debounce);
    }
  }, [representativeTruckImplementList]);

  const setSensorDataValues = truckImplementId => {
    if (sensorDataValuesDTO && sensorDataValuesDTO.length > 0) {
      return sensorDataValuesDTO.find(sd => sd.truckImplementId === truckImplementId);
    }
  };

  const getTruckOdometer = (truckImplementId: number) => {
    if (truckImplementOdometerList && truckImplementOdometerList.length > 0) {
      const odometer = truckImplementOdometerList.find(od => od.truckImplementId === truckImplementId);
      return odometer?.totalOdometer || 0;
    }
    return 0;
  };

  const sort = () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
    });
  };

  const sortByName = p => {
    setPaginationState({
      ...paginationState,
      sort: p,
    });
  };

  const sortClear = () => {
    setSearchCompanyName('');
    setSearchLicensePlate('');
    setSearchRepresentative('');
    setStatus(StatusFilter.ACTIVATED);
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
  };

  const handlePagination = (_, page) =>
    setPaginationState({
      ...paginationState,
      activePage: page,
    });

  const handleSearchCompany = (company: ICompany) => {
    setSearchCompanyName(company?.name);
  };

  const handleSearchLicensePlate = event => {
    setSearchLicensePlate(event.target.value);
  };

  const handleSearchRepresentative = (company: ICompany) => {
    setSearchRepresentative(company?.name);
  };

  const handleFavoriteClick = (event, truckImplement: ITruckImplement) => {
    event.stopPropagation();

    dispatch(createEntity(truckImplement));
    setFavorites(prev => ({
      ...prev,
      [truckImplement.id]: !prev[truckImplement.id],
    }));
  };

  const setActivePage = () => {
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
  };

  const BoxStatus = styled('div')({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '4px 16px',
    gap: '4px',
    borderRadius: '8px',
    fontWeight: '500',
    fontSize: '.75rem',
  });

  const renderCard = (truckImplement: ITruckImplement, representative?: string) => {
    const sensorDataValues = setSensorDataValues(truckImplement.id);

    return (
      <Card
        onClick={() => setSelectedTruckImplement(truckImplement)}
        sx={{
          borderRadius: '8px',
          cursor: 'pointer',
          width: '350px',
          maxWidth: '90vw',
          transition: 'transform 0.3s',
          boxShadow: '3px 3px 6px -3px rgba(0,0,0,0.5)',
          '&:hover': {
            transform: 'scale(1.05)',
          },
        }}
      >
        <CardContent>
          <Box
            sx={{
              backgroundColor: 'primary.main',
              borderRadius: '8px',
              marginBottom: '8px',
              position: 'relative',
              height: '200px',
              border: theme.palette.mode === 'dark' ? 'none' : 'solid',
              borderColor: 'secondary.light',
            }}
          >
            <IconButton
              onClick={e => handleFavoriteClick(e, truckImplement)}
              color="secondary"
              sx={{
                position: 'absolute',
                left: '16px',
                top: '10px',
                backgroundColor: alpha(theme.palette.primary.main, 0.8),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 1),
                },
              }}
            >
              {favorites[truckImplement.id] ? <Favorite sx={{ color: '#FF3040' }} /> : <FavoriteBorder sx={{ color: '#FF3040' }} />}
            </IconButton>
            {!loadingSensorDataValues || selectedTruckImplement ? (
              <BoxStatus
                sx={{
                  backgroundColor:
                    sensorDataValues?.isPowerOn && sensorDataValues?.outdated ? 'status.main' : sensorDataValues?.isPowerOn ? 'success.main' : 'error.main',
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                }}
              >
                <Typography
                  sx={{
                    color: sensorDataValues?.isPowerOn ? 'success.contrastText' : 'error.contrastText',
                    fontSize: '0.875rem',
                  }}
                >
                  {truckImplement?.activated === true
                    ? sensorDataValues?.isPowerOn && sensorDataValues?.outdated
                      ? 'Desatualizado'
                      : sensorDataValues?.isPowerOn
                      ? 'Ligado'
                      : 'Desligado'
                    : 'Inativo'}
                </Typography>
              </BoxStatus>
            ) : (
              <CircularProgress
                color="secondary"
                size={25}
                sx={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                }}
              />
            )}
            {truckImplement?.image ? (
              <CardMedia
                component="img"
                src={`data:image/*;base64,${truckImplement.image}`}
                sx={{
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '8px',
                }}
              />
            ) : (
              <NoImage color="text.secondary" />
            )}
          </Box>
          <Box
            sx={{
              backgroundColor: 'primary.main',
              marginBottom: '8px',
              borderRadius: '8px',
              padding: '16px',
              border: theme.palette.mode === 'dark' ? 'none' : 'solid',
              borderColor: 'secondary.light',
            }}
          >
            <Typography fontSize=".75rem" color="primary.contrastText" gutterBottom>
              <strong>Placa:</strong> {truckImplement?.licensePlate}
            </Typography>
            <Typography fontSize=".75rem" color="primary.contrastText" gutterBottom>
              <strong>Chassi:</strong> {truckImplement?.chassisNumber}
            </Typography>
            <Tooltip title={truckImplement?.company?.name} placement="top">
              <Typography
                fontSize=".75rem"
                color="primary.contrastText"
                overflow={'hidden'}
                whiteSpace={'nowrap'}
                textOverflow={'ellipsis'}
                gutterBottom={isManufacturerAdmin}
              >
                <strong>Empresa:</strong> {truckImplement?.company?.name}
              </Typography>
            </Tooltip>
            {representative && isManufacturerAdmin && (
              <Tooltip title={representative} placement="top">
                <Typography fontSize=".75rem" color="primary.contrastText" overflow={'hidden'} whiteSpace={'nowrap'} textOverflow={'ellipsis'}>
                  <strong>Representante:</strong> {representative}
                </Typography>
              </Tooltip>
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
            }}
          >
            <Box
              sx={{
                flexGrow: 1,
                // marginRight: '7.5px',
                borderRadius: '8px',
                border: theme.palette.mode === 'dark' ? 'none' : 'solid',
                borderColor: 'secondary.light',
              }}
            >
              <Typography>{renderIndicator('Km total', getTruckOdometer(truckImplement.id))}</Typography>
            </Box>
            {/* <Box
              sx={{
                flexGrow: 1,
                borderRadius: '8px',
                border: theme.palette.mode === 'dark' ? 'none' : 'solid',
                borderColor: 'secondary.light',
              }}
            >
              <Typography>
                {renderIndicator('Alertas', sensorDataValues && sensorDataValues.totalAlertActivation + sensorDataValues.totalVirtualFenceCircleAlert)}
              </Typography>
            </Box> */}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderIndicator = (label, value) => (
    <Box sx={{ backgroundColor: 'primary.main', borderRadius: '8px', paddingY: '16px', textAlign: 'center' }}>
      <Typography fontSize={14} color="primary.contrastText">
        {label}
      </Typography>
      {!loadingSensorDataValues || selectedTruckImplement ? (
        <Typography variant="subtitle1" fontWeight={600} color="primary.contrastText" mt={1}>
          {value ? value : 0}
        </Typography>
      ) : (
        <Typography fontSize="1rem" color="primary.contrastText" paddingTop="16px" fontWeight="400">
          <CircularProgress color="secondary" size={25} />
        </Typography>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        backgroundColor: 'primary.main',
        padding: '24px',
        borderRadius: '16px',
        boxShadow: '3px 3px 6px -3px rgba(0,0,0,0.5)',
      }}
    >
      <PageHeader
        headingId="fleetSenseApp.truckImplement.home.title"
        headingDataCy="TruckImplementHeading"
        contentKey="fleetSenseApp.truckImplement.home.title"
        buttonTextKey="fleetSenseApp.truckImplement.home.createLabel"
        match={match}
        showButton={isAdmin || isRepresentativeAdmin}
      />

      <PageFilters
        setActivePage={setActivePage}
        isAdmin={isAdmin || isManufacturerAdmin || isRepresentativeAdmin}
        isManufacturerAdmin={isManufacturerAdmin}
        status={status}
        setStatus={setStatus}
        handleSearchCompany={handleSearchCompany}
        searchCompanyName={searchCompanyName}
        searchLicensePlate={searchLicensePlate}
        handleSearchLicensePlate={handleSearchLicensePlate}
        searchRepresentative={searchRepresentative}
        handleSearchRepresentative={handleSearchRepresentative}
        loading={loading || loadingRepresentative}
        getAllEntities={getAllEntities}
        paginationState={paginationState}
        sort={sort}
        sortByName={sortByName}
        sortClear={sortClear}
        sortQuery={isManufacturerAdmin || isRepresentativeAdmin ? 'targetCompany.name' : 'name'}
        showFilters={['company', 'licensePlate', 'filterOrder', 'clearFilters', 'status', 'manufacturer']}
      />

      {selectedTruckImplement && (
        <TruckImplementModal
          sensorDataValues={setSensorDataValues(selectedTruckImplement.id)}
          implement={selectedTruckImplement}
          toggle={() => setSelectedTruckImplement(null)}
          isOpen={!!selectedTruckImplement}
        />
      )}
      {!loading && truckImplementList && truckImplementList.length > 0 && (
        <Grid container justifyContent="center" spacing={2} marginTop="4px">
          {truckImplementList.map((truckImplement, i) => (
            <Grid
              item
              xs="auto"
              key={`entity-${i}`}
              sx={{
                [theme.breakpoints.up('sm')]: {
                  width: '330px',
                },
                [theme.breakpoints.down('sm')]: {
                  width: '110%',
                },
                filter: truckImplement?.activated === true ? 'none' : 'grayscale(1)',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              {truckImplement?.id && renderCard(truckImplement)}
            </Grid>
          ))}
        </Grid>
      )}
      {!loadingRepresentative && representativeTruckImplementList && representativeTruckImplementList.length > 0 && (
        <Grid container justifyContent="center" spacing={2} marginTop="4px">
          {representativeTruckImplementList.map((representativeTruck, i) => (
            <Grid
              item
              xs="auto"
              key={`entity-${i}`}
              sx={{
                [theme.breakpoints.up('sm')]: {
                  width: '330px',
                },
                [theme.breakpoints.down('sm')]: {
                  width: '110%',
                },
                filter: representativeTruck.truckImplement?.activated === true ? 'none' : 'grayscale(1)',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              {representativeTruck?.truckImplement &&
                representativeTruck?.company?.name &&
                renderCard(representativeTruck?.truckImplement, representativeTruck?.company?.name)}
            </Grid>
          ))}
        </Grid>
      )}
      {!loading &&
        truckImplementList &&
        truckImplementList.length === 0 &&
        !loadingRepresentative &&
        representativeTruckImplementList &&
        representativeTruckImplementList.length === 0 && <NoData />}
      {(loading || loadingRepresentative) && <Loading />}
      <PaginationComponent
        paginationState={paginationState}
        totalItems={totalItems > 0 ? totalItems : totalItemsRepresentative}
        onChange={handlePagination}
        list={truckImplementList?.length > 0 ? truckImplementList : representativeTruckImplementList}
      />
    </Box>
  );
};

export default TruckImplementCard;
